import { useMemo } from 'react';
import { defaultTextStyle } from '@cinegrid/core';
import { useEditorStore } from '../../state/editorStore';
import { PanelShell, SectionLabel, SliderRow } from '../controls/PanelShell';

export function TextPanel() {
  const selection = useEditorStore((s) => s.selection);
  const project = useEditorStore((s) => s.project);
  const updateClipProperties = useEditorStore((s) => s.updateClipProperties);
  const addTextClip = useEditorStore((s) => s.addTextClip);
  const playhead = useEditorStore((s) => s.playhead);

  const clip = useMemo(
    () => project.clips.find((c) => c.id === selection[0] && c.kind === 'text'),
    [project.clips, selection]
  );

  const videoTrack = project.tracks.find((t) => t.kind === 'video');

  if (!clip) {
    return (
      <PanelShell title="Text">
        <div className="p-3">
          <p className="mb-3 text-2xs text-text-low">No text clip selected.</p>
          <button
            type="button"
            className="border border-accent bg-raised px-3 py-1.5 text-2xs text-accent hover:bg-accent hover:text-base"
            onClick={() => videoTrack && addTextClip(videoTrack.id, playhead)}
          >
            Add Text at Playhead
          </button>
        </div>
      </PanelShell>
    );
  }

  const text = clip.properties.text ?? defaultTextStyle();
  const set = (partial: Partial<typeof text>) =>
    updateClipProperties(clip.id, { text: { ...text, ...partial } });

  return (
    <PanelShell title="Text">
      <SectionLabel>Content</SectionLabel>
      <textarea
        className="m-2 min-h-[72px] w-[calc(100%-1rem)] resize-y border border-hairline bg-base p-2 text-xs text-text-high outline-none focus:border-accent"
        value={text.content}
        onChange={(e) => set({ content: e.target.value })}
      />
      <SectionLabel>Style</SectionLabel>
      <label className="grid grid-cols-[72px_1fr] items-center gap-2 px-2 py-1 text-2xs">
        <span className="text-text-low">Font</span>
        <select
          className="border border-hairline bg-raised px-1 py-1"
          value={text.fontFamily}
          onChange={(e) => set({ fontFamily: e.target.value })}
        >
          <option>IBM Plex Sans</option>
          <option>JetBrains Mono</option>
          <option>Georgia</option>
          <option>Impact</option>
        </select>
      </label>
      <SliderRow label="Size" value={text.fontSize} min={12} max={200} step={1} onChange={(fontSize) => set({ fontSize })} format={(v) => `${v | 0}`} />
      <SliderRow label="Weight" value={text.fontWeight} min={300} max={800} step={100} onChange={(fontWeight) => set({ fontWeight })} format={(v) => `${v | 0}`} />
      <label className="grid grid-cols-[72px_1fr] items-center gap-2 px-2 py-1 text-2xs">
        <span className="text-text-low">Color</span>
        <input type="color" value={text.color} onChange={(e) => set({ color: e.target.value })} />
      </label>
      <label className="grid grid-cols-[72px_1fr] items-center gap-2 px-2 py-1 text-2xs">
        <span className="text-text-low">Align</span>
        <select
          className="border border-hairline bg-raised px-1 py-1"
          value={text.align}
          onChange={(e) => set({ align: e.target.value as typeof text.align })}
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </label>
      <SliderRow label="Stroke" value={text.strokeWidth} min={0} max={12} step={0.5} onChange={(strokeWidth) => set({ strokeWidth })} />
    </PanelShell>
  );
}
